import { EnhancedDimension } from '@factorialco/deprecated-design-system'
import { IconType } from '@factorialco/f0-react'
import {
  EntityId,
  EntitySelect,
  EntitySelectEntity,
  EntitySelectProps,
} from '@factorialco/f0-react/dist/experimental'

import { EntityGroup } from './types'

export type Action = {
  label: string
  onClick: () => void
  icon?: IconType
  variant?: 'default' | 'outline' | 'critical' | 'neutral' | 'ghost' | 'promote' | 'outlinePromote'
  disabled?: boolean
}

type Props<T> = Omit<EntitySelectProps<T>, 'onItemExpandedChange' | 'width'> & {
  applySearchToGroupIn?: string[]
  children?: React.ReactNode
  entities: EntitySelectEntity[]
  groups: EntityGroup[]
  expandedElements: EntityId[]
  selectedGroup: string
  selectedGroupedEntities: EntitySelectEntity[]
  loading?: boolean
  minWidth?: EnhancedDimension
  multiple?: boolean
  width?: EnhancedDimension
  onOpen?: () => void
  onPopoverClose?: () => void
  onSelect: (selection: EntitySelectEntity | null) => void
  onSelectMultiple: (selection: EntitySelectEntity[] | null) => void
  onGroupChange?: (group: string) => void
  onSelectExpandedElements: (elements: EntityId[]) => void
  actions?: Action[]
}

export const EntitySelectWrapper = <T,>(props: Props<T>) => {
  const { expandedElements, selectedGroup, onGroupChange, onSelectExpandedElements } = props

  const onOpenChange = (openParam: boolean) => {
    if (openParam) {
      props.onOpen && props.onOpen()
    }

    if (props.onPopoverClose && !openParam) {
      props.onPopoverClose()
    }
  }

  const onSelectNullSelection = () => {
    if (!props.multiple) {
      props.onSelect(null)
    } else {
      props.onSelectMultiple([])
    }
  }

  const onSelect = (selection: EntitySelectEntity[] | EntitySelectEntity | null) => {
    if (selection === null) {
      onSelectNullSelection()
    } else if (Array.isArray(selection) && !!props.multiple) {
      props.onSelectMultiple(selection)
    } else if (!Array.isArray(selection) && !props.multiple) {
      props.onSelect(selection)
    }
  }

  const handleGroupChange = (value: string | null) => {
    onGroupChange(value)
  }

  const onItemExpandedChange = (id: EntityId, expanded: boolean) => {
    if (expanded) {
      onSelectExpandedElements([id].concat(expandedElements))
    } else {
      onSelectExpandedElements(expandedElements.filter((el) => el !== id))
    }
  }

  const preferredWidth = props.width ?? props.minWidth
  const finalWidth = preferredWidth ? Number(preferredWidth.replace('s', '')) : undefined

  const applySearchToGroup = props.applySearchToGroupIn?.includes(selectedGroup)

  const singleSelector = !props.multiple

  return (
    <EntitySelect
      {...props}
      entities={props.entities || []}
      selectedEntities={props.selectedGroupedEntities}
      selectedGroup={selectedGroup}
      onOpenChange={onOpenChange}
      onGroupChange={handleGroupChange}
      onSelect={onSelect}
      singleSelector={singleSelector}
      width={finalWidth}
      onItemExpandedChange={onItemExpandedChange}
      applySearchToGroup={applySearchToGroup}
      actions={props.actions}
    >
      {props.children}
    </EntitySelect>
  )
}
